/**
 * The MFA challenge on a new admin device (ARCHITECTURE §9.6). Shows the 4-digit code + request id
 * for the operator to read to a trusted device, polls until approved/denied/expired, and offers the
 * TOTP fallback. A session is issued only after the claim (poll → APPROVED).
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { mfaPoll, mfaTotp } from './api.js';

export function MfaChallenge({
  request,
  onApproved,
  onCancel,
}: {
  request: { shortId: string; code: string; expiresAt: number };
  onApproved: () => void;
  onCancel: () => void;
}): ReactNode {
  const [status, setStatus] = useState<'PENDING' | 'DENIED' | 'EXPIRED'>('PENDING');
  const [totp, setTotp] = useState('');
  const [totpError, setTotpError] = useState(false);
  const done = useRef(false);

  useEffect(() => {
    let alive = true;
    const tick = async (): Promise<void> => {
      if (done.current) return;
      const s = await mfaPoll();
      if (!alive) return;
      if (s === 'APPROVED') {
        done.current = true;
        onApproved();
      } else if (s === 'DENIED') setStatus('DENIED');
      else if (s === 'EXPIRED' || s === 'UNKNOWN') setStatus('EXPIRED');
    };
    const id = window.setInterval(() => void tick(), 2000);
    void tick();
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [onApproved]);

  const submitTotp = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setTotpError(false);
    if (await mfaTotp(totp.replace(/\s/g, ''))) {
      done.current = true;
      onApproved();
    } else setTotpError(true);
  };

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <h1 className="auth-title">Approve this device</h1>
        {status === 'PENDING' ? (
          <>
            <p className="auth-note">On one of your trusted devices, approve request <strong>{request.shortId}</strong> and type this code:</p>
            <div className="mfa-code" data-testid="mfa-code">{request.code}</div>
            <p className="auth-hint">Waiting for approval…</p>
            <form className="mfa-totp" onSubmit={submitTotp}>
              <span className="auth-hint">Or enter a code from your authenticator app:</span>
              <div className="mfa-totp-row">
                <input inputMode="numeric" placeholder="000000" value={totp} onChange={(e) => setTotp(e.target.value)} data-testid="mfa-totp" />
                <button type="submit" disabled={totp.length < 6}>Verify</button>
              </div>
              {totpError ? <span className="auth-error">Invalid or expired code.</span> : null}
            </form>
          </>
        ) : (
          <>
            <p className="auth-error" role="alert">{status === 'DENIED' ? 'The request was denied.' : 'The request expired.'}</p>
            <button className="auth-submit" onClick={onCancel}>Try again</button>
          </>
        )}
      </div>
    </div>
  );
}
